import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Shell, PageHeader } from '../components/Shell.jsx';
import { Score } from '../components/Score.jsx';

const STATUS_LABELS = {
  discovered: { label: 'Discovered', cls: 'badge' },
  enriched:   { label: 'Enriched',   cls: 'badge' },
  scored:     { label: 'Scored',     cls: 'badge' },
  drafted:    { label: 'Drafted',    cls: 'badge badge-accent' },
  approved:   { label: 'Approved',   cls: 'badge badge-warning' },
  sent:       { label: 'Sent',       cls: 'badge badge-warning' },
  opened:     { label: 'Opened',     cls: 'badge badge-warning' },
  replied:    { label: 'Replied',    cls: 'badge badge-success' },
  booked:     { label: 'Booked',     cls: 'badge badge-success' },
  archived:   { label: 'Archived',   cls: 'badge' },
  skipped:    { label: 'Skipped',    cls: 'badge' },
  unsubscribed: { label: 'Unsub',    cls: 'badge badge-danger' },
};

export default function ProspectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [drafting, setDrafting] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function load() {
    try {
      const r = await fetch(`/api/prospects/${id}`, { credentials: 'include' });
      if (r.status === 404) { setErr('not found'); return; }
      if (!r.ok) throw new Error(await r.text());
      setData(await r.json());
    } catch (e) {
      setErr(String(e.message || e));
    }
  }

  async function draftNow() {
    if (drafting) return;
    setDrafting(true);
    try {
      const r = await fetch('/api/pitches/draft', {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prospect_id: id }),
      });
      if (r.status === 422) {
        const d = await r.json();
        alert('Draft failed validation:\n\n' + (d.failures || []).join('\n'));
        return;
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert('Draft error: ' + (d.message || 'unknown'));
        return;
      }
      const d = await r.json();
      nav(`/pitches/${d.pitch.id}/edit`);
    } catch (e) {
      alert(String(e.message || e));
    } finally {
      setDrafting(false);
    }
  }

  async function archive() {
    if (!confirm('Archive this prospect? They will not appear in the active list and we will not pitch them again.')) return;
    await fetch(`/api/prospects/${id}`, { method: 'DELETE', credentials: 'include' });
    nav('/prospects');
  }

  if (err) {
    return (
      <Shell narrow>
        <PageHeader title="Prospect not found" sub={err} />
        <Link to="/prospects" className="btn btn-secondary">Back to prospects</Link>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell><div className="row muted"><span className="spinner" /> Loading prospect</div></Shell>
    );
  }

  const p = data.prospect;
  const status = STATUS_LABELS[p.status] || { label: p.status, cls: 'badge' };
  const canDraft = !['drafted','approved','sent','opened','replied','booked','unsubscribed','archived'].includes(p.status) || p.status === 'scored' || p.status === 'enriched' || p.status === 'discovered';

  return (
    <Shell>
      <PageHeader
        eyebrow={p.outlet || 'No outlet recorded'}
        title={p.name}
        sub={[p.role, p.beat].filter(Boolean).join(' · ') || null}
        right={
          <>
            <button onClick={draftNow} className="btn" disabled={drafting}>
              {drafting ? <><span className="spinner" /> Drafting</> : 'Draft a pitch'}
            </button>
            <Link to={`/prospects/${id}/edit`} className="btn btn-secondary btn-sm">Edit</Link>
            <button onClick={archive} className="btn btn-ghost btn-sm">Archive</button>
          </>
        }
      />

      <div className="grid-2 stack-6">
        <div className="stack-4">
          <div className="card card-pad">
            <div className="row row-between" style={{ marginBottom: 'var(--space-3)' }}>
              <h3>Identity</h3>
              <div className="row row-tight">
                <Score value={p.fit_score} />
                <span className={status.cls}>{status.label}</span>
              </div>
            </div>
            <Definition>
              <Term>Email</Term>
              <Detail>{p.email || <span className="faint">missing</span>} {p.email_verified ? <span className="badge badge-success" style={{ marginLeft: 6 }}>verified</span> : null}</Detail>
              <Term>Outlet URL</Term>
              <Detail>{p.outlet_url ? <a href={p.outlet_url} target="_blank" rel="noreferrer">{p.outlet_url}</a> : <span className="faint">not set</span>}</Detail>
              <Term>LinkedIn</Term>
              <Detail>{p.linkedin_url ? <a href={p.linkedin_url} target="_blank" rel="noreferrer">{p.linkedin_url}</a> : <span className="faint">not set</span>}</Detail>
              <Term>Twitter</Term>
              <Detail>{p.twitter_handle ? <a href={`https://twitter.com/${p.twitter_handle}`} target="_blank" rel="noreferrer">@{p.twitter_handle}</a> : <span className="faint">not set</span>}</Detail>
              <Term>Discovered</Term>
              <Detail>{fmtDate(p.discovered_at)} via {p.discovery_source || 'unknown'}</Detail>
              <Term>Last contacted</Term>
              <Detail>{fmtDate(p.last_contacted_at) || <span className="faint">never</span>}</Detail>
            </Definition>
          </div>

          {p.fit_reasoning ? (
            <div className="card card-pad">
              <h3 style={{ marginBottom: 'var(--space-2)' }}>Why this fit score?</h3>
              <p style={{ color: 'var(--ink-soft)', fontSize: 14 }}>{p.fit_reasoning}</p>
            </div>
          ) : null}

          <div className="card card-pad">
            <h3 style={{ marginBottom: 'var(--space-2)' }}>Why they are a prospect</h3>
            <p style={{ fontSize: 14, lineHeight: 1.55 }}>{p.evidence || <span className="faint">no evidence captured</span>}</p>
          </div>

          {p.notes ? (
            <div className="card card-pad">
              <h3 style={{ marginBottom: 'var(--space-2)' }}>Notes</h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-soft)' }}>{p.notes}</p>
            </div>
          ) : null}
        </div>

        <div className="stack-4">
          <div className="card card-pad">
            <h3 style={{ marginBottom: 'var(--space-3)' }}>Pitch history ({data.pitches.length})</h3>
            {data.pitches.length === 0 ? (
              <p className="muted" style={{ fontSize: 14 }}>No pitches yet. <button className="btn-link" onClick={draftNow} style={{ background: 'none', border: 0, color: 'var(--accent)', padding: 0, cursor: 'pointer' }}>Draft one →</button></p>
            ) : (
              <div className="stack-3">
                {data.pitches.map(pitch => (
                  <Link key={pitch.id} to={`/pitches/${pitch.id}/edit`} style={{ display: 'block', padding: '12px', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius)', textDecoration: 'none', color: 'inherit' }}>
                    <div className="row row-between">
                      <div className="mono" style={{ fontSize: 13 }}>{pitch.subject}</div>
                      <span className={STATUS_LABELS[pitch.status]?.cls || 'badge'}>{STATUS_LABELS[pitch.status]?.label || pitch.status}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {pitch.is_followup ? 'Follow-up · ' : ''}{fmtDate(pitch.drafted_at)}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <h3 style={{ marginBottom: 'var(--space-3)' }}>Coverage ({data.coverage.length})</h3>
            {data.coverage.length === 0 ? (
              <p className="muted" style={{ fontSize: 14 }}>No coverage recorded yet. Discovery routines will populate this.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.coverage.map(c => (
                  <li key={c.id} style={{ marginBottom: 8, fontSize: 14 }}>
                    {c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.title}</a> : c.title}
                    {c.published_at ? <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>{fmtDate(c.published_at)}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card card-pad">
            <h3 style={{ marginBottom: 'var(--space-3)' }}>Activity</h3>
            {data.activity.length === 0 ? (
              <p className="muted" style={{ fontSize: 14 }}>Nothing logged.</p>
            ) : (
              <div className="stack-2">
                {data.activity.slice(0, 12).map((a, i) => (
                  <div key={i} className="row" style={{ alignItems: 'flex-start', gap: 12, fontSize: 13 }}>
                    <span className="label-sm" style={{ minWidth: 60, color: 'var(--ink-faint)', paddingTop: 2 }}>{fmtTime(a.created_at)}</span>
                    <span><strong>{a.event_type}</strong> {a.description ? <span className="muted">— {a.description}</span> : null}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Definition({ children }) {
  const arr = Array.isArray(children) ? children : [children];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', columnGap: 16, rowGap: 8, fontSize: 14 }}>
      {arr}
    </div>
  );
}
function Term({ children }) { return <div className="label-sm" style={{ color: 'var(--ink-muted)', paddingTop: 2 }}>{children}</div>; }
function Detail({ children }) { return <div>{children}</div>; }

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
